import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabaseClient';
import { loadStripe } from '@stripe/stripe-js';
import { toast } from 'sonner';
import type { 
  BillingUsage, 
  PaymentMethod, 
  AutoRefillSettings,
  StripeInvoice 
} from '@/types/billing';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

// Hook to get billing usage and credits
export function useBillingUsage() {
  return useQuery<BillingUsage>({
    queryKey: ['billing-usage'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/billing-usage`,
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch billing usage');
      }

      return response.json();
    },
    refetchInterval: 60000, // Refetch every minute
  });
}

// Hook to manage payment methods
export function usePaymentMethods() {
  const queryClient = useQueryClient();

  const query = useQuery<{ paymentMethods: PaymentMethod[]; savedMethods: PaymentMethod[]; hasStripeCustomer: boolean }>({
    queryKey: ['payment-methods'],
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry if it's just a missing customer (expected case)
      if (error instanceof Error && error.message.includes('No Stripe customer found')) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000), // Exponential backoff
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-manage-payment-methods`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ action: 'list' }),
            signal: controller.signal,
          }
        );

        clearTimeout(timeoutId);

        if (!response.ok) {
          const error = await response.json();
          // Handle the case where no Stripe customer exists
          if (error.error && error.error.includes('No Stripe customer found')) {
            return {
              paymentMethods: [],
              savedMethods: [],
              hasStripeCustomer: false,
            };
          }
          throw new Error(error.error || 'Failed to fetch payment methods');
        }

        const data = await response.json();
        return {
          ...data,
          hasStripeCustomer: true,
        };
      } catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === 'AbortError') {
          throw new Error('Request timed out. Please try again.');
        }
        throw error;
      }
    },
  });

  const addPaymentMethod = useMutation({
    mutationFn: async (paymentMethodId: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-manage-payment-methods`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ action: 'add', paymentMethodId }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to add payment method');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
      queryClient.invalidateQueries({ queryKey: ['billing-usage'] });
      toast.success('Payment method added successfully');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const removePaymentMethod = useMutation({
    mutationFn: async (paymentMethodId: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-manage-payment-methods`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ action: 'remove', paymentMethodId }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to remove payment method');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
      toast.success('Payment method removed');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const setDefaultPaymentMethod = useMutation({
    mutationFn: async (paymentMethodId: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-manage-payment-methods`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ action: 'set_default', paymentMethodId }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to set default payment method');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
      toast.success('Default payment method updated');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  return {
    ...query,
    addPaymentMethod,
    removePaymentMethod,
    setDefaultPaymentMethod,
  };
}

// Hook to create Stripe customer
export function useCreateStripeCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      console.log('Creating Stripe customer...', {
        url: `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-create-customer`,
        hasToken: !!session.access_token
      });

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 20000); // 20 second timeout

      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-create-customer`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
            signal: controller.signal,
          }
        );

        clearTimeout(timeoutId);

        console.log('Stripe customer creation response:', {
          status: response.status,
          ok: response.ok,
          statusText: response.statusText
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({ 
            error: `HTTP ${response.status}: ${response.statusText}` 
          }));
          
          console.error('Stripe customer creation failed:', {
            status: response.status,
            error,
            url: `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-create-customer`,
            token: session.access_token ? 'Present' : 'Missing'
          });
          
          throw new Error(error.error || `Failed to create Stripe customer (${response.status})`);
        }

        const result = await response.json();
        console.log('Stripe customer created successfully:', result);
        return result;
      } catch (error) {
        clearTimeout(timeoutId);
        
        if (error instanceof Error && error.name === 'AbortError') {
          console.error('Stripe customer creation timed out');
          throw new Error('Request timed out. Please check your connection and try again.');
        }
        
        console.error('Error creating Stripe customer:', error);
        throw error;
      }
    },
    retry: (failureCount, error) => {
      // Don't retry for authentication errors or timeout errors
      if (error instanceof Error && 
          (error.message.includes('Unauthorized') || 
           error.message.includes('Not authenticated') ||
           error.message.includes('timeout'))) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000), // Exponential backoff
         onSuccess: (data) => {
       console.log('Stripe customer creation successful, invalidating queries:', data);
       
       // Invalidate and refetch related queries
       queryClient.invalidateQueries({ queryKey: ['stripe-customer'] });
       queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
       queryClient.invalidateQueries({ queryKey: ['billing-usage'] });
       
       // Force refetch payment methods to update hasStripeCustomer
       queryClient.refetchQueries({ queryKey: ['payment-methods'] });
     },
    onError: (error) => {
      console.error('Stripe customer creation failed:', error);
    },
  });
}

// Hook to purchase boost pack
export function usePurchaseBoostPack() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ credits, paymentMethodId, expectedPrice }: { credits: number; paymentMethodId?: string; expectedPrice: number }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-create-boost-pack-charge`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ credits, paymentMethodId, expectedPrice }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to purchase boost pack');
      }

      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['billing-usage'] });
      queryClient.invalidateQueries({ queryKey: ['credit-transactions'] });
      toast.success(`Successfully purchased ${data.credits} credits!`);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
}

// Hook to manage auto-refill settings
export function useAutoRefillSettings() {

  const query = useQuery<AutoRefillSettings | null>({
    queryKey: ['auto-refill-settings'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('auto_refill_settings')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
  });

  const updateSettings = useMutation({
    mutationFn: async (settings: Partial<AutoRefillSettings>) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // First check if settings exist
      const { data: existingSettings } = await supabase
        .from('auto_refill_settings')
        .select('id')
        .eq('user_id', user.id)
        .single();

      let result;
      if (existingSettings) {
        // Update existing settings
        const { data, error } = await supabase
          .from('auto_refill_settings')
          .update({
            ...settings,
            updated_at: new Date().toISOString(),
          })
          .eq('user_id', user.id)
          .select()
          .single();
        
        if (error) throw error;
        result = data;
      } else {
        // Insert new settings
        const { data, error } = await supabase
          .from('auto_refill_settings')
          .insert({
            user_id: user.id,
            ...settings,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .select()
          .single();
        
        if (error) throw error;
        result = data;
      }

      return result;
    },
    onError: (error) => {
      toast.error(error.message);
    },
    // Don't invalidate queries - let the component manage its own state
  });

  return {
    ...query,
    updateSettings,
  };
}

// Hook to get invoices - DEPRECATED
// Invoice data is now accessed directly through Stripe dashboard
// Keeping this for backward compatibility but returns empty array
export function useInvoices() {
  return useQuery<StripeInvoice[]>({
    queryKey: ['invoices'],
    queryFn: async () => {
      // We no longer store invoice data in the database
      // Users can access their invoice history through the Stripe customer portal
      return [];
    },
  });
}

// Hook to get comprehensive billing history with pagination
export function useBillingHistory(
  page: number = 1,
  pageSize: number = 50,
  dateRange?: { startDate: string; endDate: string }
) {
  return useQuery({
    queryKey: ['billing-history', page, pageSize, dateRange],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Build query with pagination and date filtering
      // Primary approach: complex OR filter for all relevant transaction types
      // FIXED: Add count: 'exact' for proper pagination counts
      let query = supabase
        .from('credit_transactions')
        .select('*', { count: 'exact' })
        .eq('user_id', user.id);

      // Simplified filtering - use IN for main transaction types
      query = query
        .in('transaction_type', ['boost_pack_purchase', 'subscription_grant', 'subscription_payment', 'auto_refill', 'manual_adjustment'])
        .order('created_at', { ascending: false })
        .range((page - 1) * pageSize, page * pageSize - 1);

      // Add date filtering if provided
      if (dateRange) {
        query = query
          .gte('created_at', dateRange.startDate)
          .lte('created_at', dateRange.endDate);
      }

      // FIXED: Proper error handling around query execution
      let result;
      try {
        result = await query;
      } catch (queryError) {
        console.warn('Primary OR query failed, attempting fallback query:', queryError);
                 // FIXED: Fallback query uses broader filter, corrected by in-memory filtering
         // Using broader filter since if OR syntax failed, we can't rely on complex OR in fallback
         let fallbackQuery = supabase
           .from('credit_transactions')
           .select('*', { count: 'exact' })
           .eq('user_id', user.id)
           .in('transaction_type', ['boost_pack_purchase', 'subscription_grant', 'subscription_payment', 'auto_refill', 'manual_adjustment'])
           .order('created_at', { ascending: false })
           .range((page - 1) * pageSize, page * pageSize - 1);

        if (dateRange) {
          fallbackQuery = fallbackQuery
            .gte('created_at', dateRange.startDate)
            .lte('created_at', dateRange.endDate);
        }

        result = await fallbackQuery;
      }

      const { data: transactions, error: transError, count } = result;

      if (transError) throw transError;

      // Get boost pack details for purchases (only for this page)
      const boostPackIds = transactions
        ?.filter(t => t.transaction_type === 'boost_pack_purchase' && t.reference_id)
        .map(t => t.reference_id) || [];

      let boostPacks: Array<{
        id: string;
        user_id: string;
        credits: number;
        credits_remaining: number;
        amount_paid: number;
        stripe_charge_id?: string;
        purchased_at: string;
        expires_at?: string;
        created_at: string;
      }> = [];
      
      if (boostPackIds.length > 0) {
        const { data: packs, error: packError } = await supabase
          .from('boost_packs')
          .select('*')
          .in('id', boostPackIds);

        if (packError) throw packError;
        boostPacks = packs || [];
      }

      // Filter transactions client-side for reliability and proper logging
      const filteredTransactions = transactions?.filter(transaction => {
        // Manual adjustments: only show credit expirations
        if (transaction.transaction_type === 'manual_adjustment') {
          return transaction.reference_type === 'expiration';
        }
        
        // Subscription payments: only show if actual money was paid
        if (transaction.transaction_type === 'subscription_payment') {
          const hasPayment = Number(transaction.amount_paid || 0) > 0;
          console.log(`[BILLING] subscription_payment filter:`, { 
            amount_paid: transaction.amount_paid, 
            hasPayment,
            description: transaction.description 
          });
          return hasPayment;
        }
        
        // Credit transactions: only show if credits were moved
        if (['boost_pack_purchase', 'subscription_grant', 'auto_refill'].includes(transaction.transaction_type)) {
          return Number(transaction.credits_amount || 0) !== 0;
        }
        
        return false;
      });

      // Combine and format the data
      const history = filteredTransactions?.map(transaction => {
        if (transaction.transaction_type === 'boost_pack_purchase') {
          const boostPack = boostPacks.find(bp => bp.id === transaction.reference_id);
          return {
            id: transaction.id,
            type: 'boost_pack',
            date: transaction.created_at,
            description: transaction.description || `${transaction.credits_amount} Credit Boost Pack`,
            amount: boostPack?.amount_paid || 0,
            credits: Number(transaction.credits_amount),
            status: 'completed',
            stripe_charge_id: boostPack?.stripe_charge_id,
          };
        } else if (transaction.transaction_type === 'subscription_grant') {
          return {
            id: transaction.id,
            type: 'subscription_credit',
            date: transaction.created_at,
            description: transaction.description || 'Monthly Credit Allocation',
            amount: 0, // Subscription grants don't have a direct payment
            credits: Number(transaction.credits_amount),
            status: 'completed',
          };
        } else if (transaction.transaction_type === 'subscription_payment') {
          return {
            id: transaction.id,
            type: 'subscription_payment',
            date: transaction.created_at,
            description: transaction.description || 'Subscription Payment',
            amount: Number(transaction.amount_paid) || 0, // Use the stored payment amount
            credits: 0, // Payments don't directly add credits
            status: 'completed',
          };
        } else if (transaction.transaction_type === 'auto_refill') {
          return {
            id: transaction.id,
            type: 'auto_refill',
            date: transaction.created_at,
            description: transaction.description || 'Auto-Refill',
            amount: 0, // Will need to fetch from boost pack if needed
            credits: Number(transaction.credits_amount),
            status: 'completed',
          };
        } else if (transaction.transaction_type === 'manual_adjustment' && transaction.reference_type === 'expiration') {
          return {
            id: transaction.id,
            type: 'credit_expiration',
            date: transaction.created_at,
            description: transaction.description || 'Free trial credits expired',
            amount: 0,
            credits: Number(transaction.credits_amount),
            status: 'completed',
          };
        }
        return null;
      }).filter(Boolean) || [];

      // FIXED: Use server count for honest total, show actual results per page
      // Note: Server count includes all matching rows across pages (may include filtered items)
      const serverTotal = count || 0;
      const actualHistoryLength = history?.length || 0;
      
      // Calculate display range based on actual results on this page
      const displayStart = actualHistoryLength > 0 ? ((page - 1) * pageSize) + 1 : 0;
      const displayEnd = actualHistoryLength > 0 ? displayStart + actualHistoryLength - 1 : 0;

      return {
        history: history as Array<{
          id: string;
          type: string;
          date: string;
          description: string;
          amount: number;
          credits: number;
          status: string;
          stripe_charge_id?: string;
        }>,
        pagination: {
          page,
          pageSize,
          total: serverTotal,
          totalPages: Math.ceil(serverTotal / pageSize),
          hasNextPage: page < Math.ceil(serverTotal / pageSize),
          hasPrevPage: page > 1,
          // Add display range for UI
          displayStart,
          displayEnd,
          actualResults: actualHistoryLength,
          isFiltered: true, // Indicates this is a filtered view
        }
      };
    },
  });
}

// Hook to get billing history summary (for dashboard cards)
export function useBillingHistorySummary(dateRange?: { startDate: string; endDate: string }) {
  return useQuery({
    queryKey: ['billing-history-summary', dateRange],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Build query for summary
      let query = supabase
        .from('credit_transactions')
        .select('transaction_type, credits_amount, created_at, reference_type')
        .eq('user_id', user.id);

      // Add the OR filter for transaction types
      // FIXED: Removed space after comma to prevent PostgREST 400 error
      query = query.or('transaction_type.in.(boost_pack_purchase,subscription_grant,subscription_payment,auto_refill),and(transaction_type.eq.manual_adjustment,reference_type.eq.expiration)');

      // Add date filtering if provided
      if (dateRange) {
        query = query
          .gte('created_at', dateRange.startDate)
          .lte('created_at', dateRange.endDate);
      }

      // FIXED: Proper error handling for summary query too
      let summaryResult;
      try {
        summaryResult = await query;
      } catch (queryError) {
                 console.warn('Summary OR query failed, attempting fallback query:', queryError);
         // FIXED: Fallback query for summary uses broader filter, corrected by in-memory filtering
         let fallbackQuery = supabase
           .from('credit_transactions')
           .select('transaction_type, credits_amount, created_at, reference_type')
           .eq('user_id', user.id)
           .in('transaction_type', ['boost_pack_purchase', 'subscription_grant', 'subscription_payment', 'auto_refill', 'manual_adjustment']);

        if (dateRange) {
          fallbackQuery = fallbackQuery
            .gte('created_at', dateRange.startDate)
            .lte('created_at', dateRange.endDate);
        }

        summaryResult = await fallbackQuery;
      }

      const { data: transactions, error } = summaryResult;
      if (error) throw error;

      // Calculate summary statistics
      // FIXED: Use filtered transactions for consistent counts
      const summary = {
        totalTransactions: 0, // Will be set from filtered transactions
        totalCreditsPurchased: 0,
        totalCreditsGranted: 0,
        totalCreditsExpired: 0,
        totalAmountSpent: 0,
        boostPackPurchases: 0,
        subscriptionGrants: 0,
        autoRefills: 0,
        creditExpirations: 0,
      };

      // Filter transactions for fallback case and calculate summary
      const filteredSummaryTransactions = transactions?.filter(transaction => {
        if (transaction.transaction_type === 'manual_adjustment') {
          return transaction.reference_type === 'expiration';
        }
        return ['boost_pack_purchase', 'subscription_grant', 'auto_refill'].includes(transaction.transaction_type);
      });

      filteredSummaryTransactions?.forEach(transaction => {
        const credits = Number(transaction.credits_amount);
        
        if (transaction.transaction_type === 'manual_adjustment' && transaction.reference_type === 'expiration') {
          // Handle credit expiration (negative amount)
          summary.totalCreditsExpired += Math.abs(credits);
          summary.creditExpirations++;
        } else {
          switch (transaction.transaction_type) {
            case 'boost_pack_purchase':
              summary.totalCreditsPurchased += credits;
              summary.boostPackPurchases++;
              break;
            case 'subscription_grant':
              summary.totalCreditsGranted += credits;
              summary.subscriptionGrants++;
              break;
            case 'auto_refill':
              summary.totalCreditsPurchased += credits;
              summary.autoRefills++;
              break;
          }
        }
      });

      // FIXED: Set total transactions from filtered count for consistency
      summary.totalTransactions = filteredSummaryTransactions?.length || 0;

      return summary;
    },
  });
}

// Hook to create payment method setup
export async function createPaymentMethodSetup() {
  const stripe = await stripePromise;
  if (!stripe) throw new Error('Stripe not loaded');

  // Create customer if needed
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const customerResponse = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-create-customer`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!customerResponse.ok) {
    throw new Error('Failed to create Stripe customer');
  }

  return { stripe };
} 